import httpClient from './auth-http-client'

export interface FeedbackAttachment {
    name: string
    mime_type: string
    size_bytes: number
    bucket: string
    path: string
    error?: string
}

export interface FeedbackPayload {
    feedback: string
    page?: string
    attachments?: FeedbackAttachment[]
    /** What kind of feedback this is, e.g. 'ai_report' (src/agent/aiReport.ts). Omitted for general feedback. */
    kind?: string
    /** Structured details for that kind. */
    context?: Record<string, unknown>
}

export interface ClientErrorPayload {
    message: string
    stack?: string
    url?: string
    context?: Record<string, unknown>
}

export async function uploadFeedbackAttachments(
    files: File[],
): Promise<FeedbackAttachment[]> {
    const formData = new FormData()
    for (const f of files) {
        formData.append('files', f, f.name)
    }
    const response = await httpClient.post('/feedback/attachments', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data?.attachments ?? []
}

export async function submitFeedback(payload: FeedbackPayload) {
    const response = await httpClient.post('/feedback', payload)
    return response.data
}

export interface TrialFeedbackPayload {
    /** Usefulness score, 1-5. */
    rating: number
    /** Quick-pick tags from the follow-up step. */
    tags?: string[]
    comment?: string
    stage?: 'trial_ending' | 'post_trial'
    /** Negative once the trial has already ended. */
    days_to_trial_end?: number | null
}

/** End-of-trial usefulness survey. Stored as `user_feedback.category='trial_feedback'`. */
export async function submitTrialFeedback(payload: TrialFeedbackPayload) {
    const response = await httpClient.post('/feedback/trial', payload)
    return response.data
}

export type FeedbackStatus =
    | 'open'
    | 'in_progress'
    | 'planned'
    | 'completed'
    | 'declined'

export interface FeedbackAttachmentView {
    name: string | null
    mime_type: string | null
    size_bytes: number | null
    url: string | null
}

export interface FeedbackReply {
    id: string
    author_role: 'user' | 'admin'
    author_name: string | null
    body: string | null
    attachments: FeedbackAttachmentView[]
    created_at: string
    read_by_user: boolean
}

export interface FeedbackThreadSummary {
    id: string
    created_at: string
    last_activity_at: string
    status: FeedbackStatus
    page: string | null
    feedback: string | null
    preview: string | null
    attachment_count: number
    reply_count: number
    has_team_reply: boolean
    team_reply_preview: string | null
    unread_count: number
    last_reply_preview: string | null
    last_reply_author_role: 'user' | 'admin' | null
}

export interface FeedbackThread {
    id: string
    created_at: string
    last_activity_at: string
    status: FeedbackStatus
    page: string | null
    feedback: string | null
    attachments: FeedbackAttachmentView[]
    replies: FeedbackReply[]
}

export async function fetchFeedbackThreads(): Promise<FeedbackThreadSummary[]> {
    const response = await httpClient.get('/feedback/threads')
    return response.data?.threads ?? []
}

export async function fetchFeedbackThread(id: string): Promise<FeedbackThread> {
    const response = await httpClient.get(`/feedback/threads/${id}`)
    return response.data
}

export async function fetchFeedbackUnreadCount(): Promise<number> {
    const response = await httpClient.get('/feedback/unread-count')
    return response.data?.unread_count ?? 0
}

export async function markFeedbackThreadRead(id: string) {
    const response = await httpClient.post(`/feedback/threads/${id}/read`, {})
    return response.data
}

export async function replyToFeedbackThread(
    id: string,
    payload: { body: string; attachments?: FeedbackAttachment[] },
) {
    const response = await httpClient.post(
        `/feedback/threads/${id}/replies`,
        payload,
    )
    return response.data
}

export async function reportClientError(payload: ClientErrorPayload) {
    if (!payload.message?.trim()) {
        return
    }

    const response = await httpClient.post('/feedback/client-error', payload)
    return response.data
}
